package com.vizio.redwolf.m2m.api

import com.vizio.redwolf.m2m.client.M2MHttpClient
import com.vizio.redwolf.m2m.model.M2MFailureResponse
import com.vizio.redwolf.m2m.model.M2MTokenResponse
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.TimeoutException
import com.vizio.redwolf.utils.M2MEnvironment
import com.vizio.redwolf.utils.M2MEnvironmentConfig
import com.vizio.redwolf.utils.chooseM2MEnvironment
import com.vizio.redwolf.utils.clientprovider.ClientConfigFactory
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import com.vizio.redwolf.utils.retryRequest
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.http.isSuccess
import kotlin.js.JsExport

class M2MApi(environment: M2MEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig = ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS)) :
    M2MApiInterface {

    private val m2mClient = M2MHttpClient(environment, appConfig, clientConfig)

    constructor(envConfig: M2MEnvironmentConfig, appConfig: AppConfig) : this(chooseM2MEnvironment(envConfig), appConfig)

    constructor(envConfig: M2MEnvironmentConfig, appConfig: AppConfig, clientConfig: ClientConfig) : this(chooseM2MEnvironment(envConfig), appConfig, clientConfig)

    constructor(envConfig: M2MEnvironmentConfig, appConfig: AppConfig, timeout: Long) : this(chooseM2MEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS, timeout))
    override suspend fun requestM2MToken(): M2MResult<M2MTokenResponse> {
        return retryM2MRequest {
            val result = m2mClient.requestM2MToken()
            handleM2MHttpResult(result)
        }
    }

    private suspend inline fun <T : HttpResponse, reified R : Any> handleM2MHttpResult(httpResult: HttpResult<T>): M2MResult<R> {
        return when (httpResult) {
            is HttpResult.Success -> {
                val httpResponse: HttpResponse = httpResult.data
                val statusCode = httpResponse.status.value
                val headers = ResponseHeaders.Valid(statusCode)
                if (httpResponse.status.isSuccess()) {
                    try {
                        M2MResult.Success(headers, httpResponse.body())
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        M2MResult.Error(headers, ex)
                    }
                } else {
                    try {
                        val failureMessage = httpResponse.body<M2MFailureResponse>()
                        M2MResult.Failed(headers, response = failureMessage)
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        M2MResult.Error(headers, ex)
                    }
                }
            }
            is HttpResult.Error -> {
                val headers = ResponseHeaders.Invalid
                val exception = httpResult.exception
                if (exception is TimeoutException) {
                    M2MResult.Timeout(headers, exception)
                }
                else {
                    M2MResult.Error(ResponseHeaders.Invalid, exception)
                }
            }
        }
    }

    private fun <T : Any> isM2MSuccess(result: T): Boolean {
        return result is M2MResult<*>
    }

    private suspend inline fun <T: Any> retryM2MRequest(block: () -> T): T {
        return retryRequest(clientConfig, block, isSuccess = ::isM2MSuccess)
    }
}

@JsExport
sealed class M2MResult<out T : Any> {
    data class Success<out T : Any>(val headers: ResponseHeaders, val data: T) : M2MResult<T>()
    data class Failed(val headers: ResponseHeaders, val response: M2MFailureResponse) : M2MResult<Nothing>()
    data class Error(val headers: ResponseHeaders, val exception: Throwable) : M2MResult<Nothing>()
    data class Timeout(val headers: ResponseHeaders, val exception: Throwable) : M2MResult<Nothing>()

    fun <R> fold(
        onSuccess: (success: Success<T>) -> R,
        onFailed: (failed: Failed) -> R,
        onError: (error: Error) -> R,
        onTimeout: (timeout: Timeout) -> R
    ): R {
        return when (this) {
            is Success -> onSuccess(Success(this.headers, this.data))
            is Failed -> onFailed(Failed(this.headers, this.response))
            is Error -> onError(Error(this.headers, this.exception))
            is Timeout -> onTimeout(Timeout(this.headers, this.exception))
        }
    }
}